import React, { useEffect, useId, useRef } from 'react';
import { COPY } from './copy';
import ModalShell from './ModalShell';

// Shared confirm dialog for the RAI flows: centred title, design-system
// ghost Cancel + primary Send, 24px apart. Traps focus while open and restores
// it on close, and dismisses on Escape/backdrop unless mid-submit.
const ConfirmModal = ({
  open,
  onCancel,
  onConfirm,
  submitting = false,
  title = 'Please confirm.',
  zIndex = 1001,
}) => {
  const dialogRef = useRef(null);
  const cancelBtnRef = useRef(null);
  const previousActiveRef = useRef(null);

  // Latest submitting/onCancel in refs so the keydown listener can read them
  // without re-subscribing — re-subscribing on every render (onCancel is an
  // inline arrow at the call sites) would thrash the focus lifecycle below.
  const submittingRef = useRef(submitting);
  const onCancelRef = useRef(onCancel);
  useEffect(() => {
    submittingRef.current = submitting;
    onCancelRef.current = onCancel;
  });

  const titleId = useId();

  // Focus lifecycle keyed only on `open`: capture focus when the dialog opens,
  // restore it only when the dialog actually closes (not on every re-render).
  useEffect(() => {
    if (!open) return undefined;
    previousActiveRef.current = document.activeElement;
    cancelBtnRef.current?.focus();
    return () => { previousActiveRef.current?.focus?.(); };
  }, [open]);

  // Escape to dismiss + Tab/Shift+Tab focus trap, also keyed only on `open`.
  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (e.key === 'Escape') {
        if (!submittingRef.current) onCancelRef.current();
        return;
      }
      if (e.key !== 'Tab') return;
      const focusable = dialogRef.current?.querySelectorAll('button:not([disabled])');
      if (!focusable || focusable.length === 0) { e.preventDefault(); return; }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  if (!open) return null;

  const handleBackdrop = () => { if (!submitting) onCancel(); };

  return (
    <ModalShell onClose={handleBackdrop} labelledBy={titleId} zIndex={zIndex}>
      <div ref={dialogRef}>
        <h2 id={titleId} className="text-lg font-semibold mb-4 text-center">{title}</h2>
        <div className="flex flex-row gap-x-6 justify-center mt-6">
          <button
            ref={cancelBtnRef}
            type="button"
            onClick={onCancel}
            disabled={submitting}
            className="btn btn-ghost"
          >
            {COPY.buttons.cancel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={submitting}
            className="btn btn-primary"
          >
            {COPY.buttons.send}
          </button>
        </div>
      </div>
    </ModalShell>
  );
};

export default ConfirmModal;
